import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/page-hero";
import { CtaBlock } from "@/components/cta";
import { Reveal } from "@/lib/motion";
import { METODO, QUE_HACEMOS, SITE } from "@/content/site";

export default function ComoTeAcompanamos() {
  return (
    <>
      <Seo
        title="Cómo te acompañamos"
        description="Nuestro método de trabajo: primera conversación de disponibilidad, clasificación honesta de tu asunto, plan claro con costos y acompañamiento con seguimiento."
        path="/como-te-acompanamos"
      />
      <PageHero
        eyebrow="Método"
        titulo="Cómo te acompañamos"
        lead="Trabajamos igual en asuntos grandes y pequeños: claridad primero, documento después, acompañamiento siempre. Este es el camino completo, sin letra pequeña."
      />

      <section className="bg-parchment">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <h2 className="font-serif text-3xl text-ink-deep">Qué sí hacemos</h2>
                <ul className="mt-8 space-y-4">
                  {QUE_HACEMOS.hacemos.map((h, i) => (
                    <li key={i} className="flex items-start gap-3 text-ink-text" data-testid="hacemos-item">
                      <Check className="mt-1 h-4 w-4 shrink-0 text-salvia" strokeWidth={2.4} />
                      {h}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <h2 className="font-serif text-3xl text-ink-deep">Qué no hacemos</h2>
                <ul className="mt-8 space-y-4">
                  {QUE_HACEMOS.noHacemos.map((h, i) => (
                    <li key={i} className="flex items-start gap-3 text-ink-text" data-testid="no-hacemos-item">
                      <X className="mt-1 h-4 w-4 shrink-0 text-azul" strokeWidth={2.4} />
                      {h}
                    </li>
                  ))}
                </ul>
                <p className="mt-8 border-l-2 border-azul bg-azul-light/40 p-5 text-sm leading-relaxed text-ink-text">
                  {SITE.disponibilidad} Es una llamada de disponibilidad: confirmamos si tu asunto es
                  algo que podemos llevar y cómo seguir. No es asesoría jurídica completa — eso
                  empieza con un plan y un alcance firmado.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="mt-24">
            <Reveal>
              <h2 className="mb-14 font-serif text-3xl text-ink-deep sm:text-4xl">El proceso, paso por paso</h2>
            </Reveal>
            <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
              {METODO.map((paso, i) => (
                <Reveal key={paso.n} delay={i * 0.08}>
                  <div className="border-t-2 border-ink-deep pt-6">
                    <p className="font-mono text-xs tracking-[0.22em] text-azul">{paso.n}</p>
                    <h3 className="mt-4 font-serif text-2xl text-ink-deep">{paso.titulo}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-ink-muted">{paso.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal className="mt-20">
            <div className="border border-hairline bg-parchment-surface/60 p-8 sm:p-10">
              <h2 className="font-serif text-2xl text-ink-deep sm:text-3xl">Sobre costos</h2>
              <p className="mt-4 max-w-3xl text-ink-muted">
                Cada asunto es distinto, y preferimos decir costos honestos que promedios inventados.
                Después de la primera conversación y de revisar los documentos básicos, te entregamos
                un presupuesto cerrado por escrito. Nada se mueve sin tu aprobación.
              </p>
              <Link to="/contacto" data-testid="metodo-contacto-link" className="mt-6 inline-flex text-sm font-medium text-azul underline underline-offset-4">
                Empezar con la primera conversación
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBlock />
    </>
  );
}
